import React from 'react';
import {
  Badge,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@roger/ui-components';
import { X } from 'lucide-react';
import type { PermissionResponse } from '../../services/api/types.gen';

export interface PermissionBadgeProps {
  permission: PermissionResponse;
  onRemove?: () => void;
  variant?: 'outline' | 'secondary';
  className?: string;
}

/**
 * Shared badge with tooltip displaying permission name, description, and slug.
 * Supports an optional removal button for selection pickers.
 */
export const PermissionBadge = React.forwardRef<
  HTMLDivElement,
  PermissionBadgeProps
>(({ permission, onRemove, variant = 'outline', className }, ref) => {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge
          ref={ref}
          variant={variant}
          className={`cursor-help ${onRemove ? 'flex items-center gap-1 pr-1' : 'text-xs'} ${className || ''}`}
          tabIndex={0}
        >
          {permission.name}
          {onRemove && (
            <button
              type='button'
              onClick={e => {
                e.stopPropagation();
                onRemove();
              }}
              className='ml-0.5 rounded-full hover:bg-muted-foreground/20 p-0.5'
              aria-label={`Remove ${permission.name}`}
            >
              <X className='h-3 w-3' />
            </button>
          )}
        </Badge>
      </TooltipTrigger>
      <TooltipContent className='max-w-xs p-2.5'>
        <p className='font-semibold text-xs leading-snug'>{permission.name}</p>
        {permission.description && (
          <p className='mt-1 text-xs text-muted-foreground leading-normal'>
            {permission.description}
          </p>
        )}
        <p className='mt-1 font-mono text-[10px] text-muted-foreground/75'>
          {permission.slug}
        </p>
      </TooltipContent>
    </Tooltip>
  );
});

PermissionBadge.displayName = 'PermissionBadge';
